import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Box, Chip, Collapse, FormHelperText } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Cancel } from "@mui/icons-material";

import {
  StyledCheckbox,
  StyledMenuItemContainer,
  StyledMenuListItemText,
  StyledSubMenuItemContainer,
} from "../StyledComponents/styled";
import SelectFieldShell from "./SelectFieldShell";
import useSelectDropdown from "./useSelectDropdown";
import { SELECT_COLOR, SELECT_HIGHLIGHT_COLOR, SELECT_MOTION_MS } from "./selectConstants";
import { estimateOptionsContentWidth } from "./selectorOptionUtils";

// Child rows sit one indent level in (StyledSubMenuItemContainer) and carry a checkbox.
const GROUP_OPTION_INDENT_PX = 25;
const ROW_CONTROLS_WIDTH_PX = 40;

/**
 * CustomizedHierarchicalMultiselect
 *
 * Data shape (default keys):
 * [
 *   {
 *     category_name: "1-test",
 *     options: [{ value: "123", label: "prod1" }, ...]
 *   }
 * ]
 *
 * Props allow remapping keys so you can reuse with different APIs.
 * Selection uses primitive option values and the panel stays open on change.
 */
const CustomizedHierarchicalMultiselect = ({
  label,
  categories,
  value,
  onChange,
  fieldErrors = "",
  categoryLabelKey = "category_name",
  optionsKey = "options",
  optionValueKey = "value",
  optionLabelKey = "label",
  enableFullWidth = false,
}) => {
  const itemRefs = useRef({});
  const optionRefs = useRef({});

  const [expanded, setExpanded] = useState({});
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [instanceId] = useState(
    () => `hierarchical-multi-select-${Math.random().toString(36).substr(2, 9)}`
  );
  const handleDropdownClosed = useCallback(() => {
    setHighlightedIndex(-1);
  }, []);

  const dropdown = useSelectDropdown({
    instanceId,
    onClosed: handleDropdownClosed,
  });
  const {
    open,
    setOpen,
    searchTerm,
    isFocused,
    paperRef,
    close: closeDropdown,
  } = dropdown;

  // Preserve the historical close signature (refocus flag) used by the handlers.
  const close = useCallback(
    (shouldRefocus = false) =>
      closeDropdown({ clearSearch: true, refocus: shouldRefocus }),
    [closeDropdown]
  );

  // Keep a stable empty array so callbacks do not change on every render when
  // the controlled value is temporarily undefined.
  const selectedValues = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const shrink = isFocused || open || selectedValues.length > 0;

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const isSelected = useCallback(
    (v) => selectedValues.includes(v),
    [selectedValues]
  );

  // Selecting an option never closes the panel, so multiple picks stay fast.
  const handleSelect = useCallback(
    (v) => {
      const next = isSelected(v)
        ? selectedValues.filter((x) => x !== v)
        : [...selectedValues, v];
      onChange(next);
    },
    [isSelected, onChange, selectedValues]
  );

  const handleRemove = (v) => {
    onChange(selectedValues.filter((x) => x !== v));
  };

  const handleCategoryToggle = useCallback(
    (catKey) => {
      const wasExpanded = expanded[catKey];
      setExpanded((prev) => ({ ...prev, [catKey]: !prev[catKey] }));
      setHighlightedIndex(-1);

      if (!wasExpanded) {
        setTimeout(() => {
          const item = itemRefs.current[catKey];
          const container = paperRef.current;

          if (!item || !container) return;

          const itemRect = item.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          if (itemRect.bottom >= containerRect.bottom) {
            const scrollOffset = itemRect.top - containerRect.top - 8;
            container.scrollTo({
              top: container.scrollTop + scrollOffset,
              behavior: "smooth",
            });
          }
        }, 300);
      }
    },
    [expanded, paperRef]
  );

  const handleCategorySelect = (optionValues, allSelected, e) => {
    e.stopPropagation();

    if (optionValues.length === 0) return;

    let next;
    if (allSelected) {
      next = selectedValues.filter((v) => !optionValues.includes(v));
    } else {
      const toAdd = optionValues.filter((v) => !isSelected(v));
      next = [...selectedValues, ...toAdd];
    }
    onChange(next);
  };

  const sortedCategories = useMemo(() => {
    const arr = Array.isArray(categories) ? [...categories] : [];
    return arr.sort((a, b) => {
      const aLabel = String(a?.[categoryLabelKey] ?? "");
      const bLabel = String(b?.[categoryLabelKey] ?? "");

      const aNum = parseInt(aLabel.split("-")[0], 10);
      const bNum = parseInt(bLabel.split("-")[0], 10);

      const aHasNum = !Number.isNaN(aNum);
      const bHasNum = !Number.isNaN(bNum);

      if (aHasNum && bHasNum) return aNum - bNum;
      return aLabel.localeCompare(bLabel, undefined, { numeric: true });
    });
  }, [categories, categoryLabelKey]);

  const searchValue = searchTerm.trim().toLowerCase();

  const isSearchActive = searchTerm.trim().length > 0;

  // Reported to the shell, which decides the final panel width. Measured on the
  // full category list so the panel does not resize while typing.
  const dropdownContentWidth = useMemo(() => {
    const categoryHeaderWidth = estimateOptionsContentWidth(
      sortedCategories.map((category) => category?.[categoryLabelKey]),
      ROW_CONTROLS_WIDTH_PX
    );
    const optionRowWidth = estimateOptionsContentWidth(
      sortedCategories.flatMap((category) =>
        (Array.isArray(category?.[optionsKey]) ? category[optionsKey] : []).map(
          (option) => option?.[optionLabelKey]
        )
      ),
      GROUP_OPTION_INDENT_PX + ROW_CONTROLS_WIDTH_PX
    );

    return Math.max(categoryHeaderWidth, optionRowWidth);
  }, [sortedCategories, categoryLabelKey, optionsKey, optionLabelKey]);

  const optionsToList = useMemo(() => {
    return sortedCategories
      .map((cat) => {
        const allOptions = Array.isArray(cat?.[optionsKey]) ? [...cat[optionsKey]] : [];

        if (!searchValue) return cat;

        const lowerCaseCatName = String(cat?.[categoryLabelKey] ?? "").toLowerCase();
        if (lowerCaseCatName.includes(searchValue)) return cat;

        const filteredOptions = allOptions.filter((opt) => {
          const optVal = String(opt?.[optionValueKey] ?? "").toLowerCase();
          const optLabel = String(opt?.[optionLabelKey] ?? "").toLowerCase();
          return optVal.includes(searchValue) || optLabel.includes(searchValue);
        });

        if (filteredOptions.length > 0) return { ...cat, [optionsKey]: filteredOptions };

        return null;
      })
      .filter((cat) => cat !== null);
  }, [sortedCategories, searchValue, categoryLabelKey, optionsKey, optionValueKey, optionLabelKey]);

  // All selectable values across filtered categories, for the "Select All Results" row
  const filteredFlatValues = useMemo(() => {
    return optionsToList.flatMap((cat) =>
      (Array.isArray(cat?.[optionsKey]) ? cat[optionsKey] : [])
        .map((o) => o?.[optionValueKey])
        .filter((v) => v !== undefined && v !== null)
    );
  }, [optionsToList, optionsKey, optionValueKey]);

  const allFilteredSelected = useMemo(() => {
    if (filteredFlatValues.length === 0) return false;
    return filteredFlatValues.every((v) => isSelected(v));
  }, [filteredFlatValues, isSelected]);

  const someFilteredSelected = useMemo(() => {
    const count = filteredFlatValues.filter((v) => isSelected(v)).length;
    return count > 0 && !allFilteredSelected;
  }, [filteredFlatValues, isSelected, allFilteredSelected]);

  const handleSelectAllFiltered = useCallback(() => {
    if (allFilteredSelected) {
      const next = selectedValues.filter((v) => !filteredFlatValues.includes(v));
      onChange(next);
    } else {
      const toAdd = filteredFlatValues.filter((v) => !isSelected(v));
      onChange([...selectedValues, ...toAdd]);
    }
  }, [allFilteredSelected, filteredFlatValues, isSelected, selectedValues, onChange]);

  // Flat list of all keyboard-navigable items for arrow key handling
  const flatItemsList = useMemo(() => {
    const list = [];

    // Index 0 is reserved for the "Select All Results" row when search is active
    if (isSearchActive) {
      list.push({ type: "select-all-results" });
    }

    optionsToList.forEach((cat) => {
      const catKey = String(cat?.[categoryLabelKey] ?? "");
      list.push({ type: "group", catKey });

      if (expanded[catKey]) {
        const catOptions = Array.isArray(cat?.[optionsKey]) ? cat[optionsKey] : [];
        const sorted = [...catOptions].sort((a, b) => {
          const av = String(a?.[optionValueKey] ?? "");
          const bv = String(b?.[optionValueKey] ?? "");
          return av.localeCompare(bv, undefined, { numeric: true });
        });
        sorted.forEach((opt) => {
          const optValue = opt?.[optionValueKey];
          if (optValue !== undefined && optValue !== null) {
            list.push({ type: "option", optValue, catKey });
          }
        });
      }
    });

    return list;
  }, [optionsToList, expanded, isSearchActive, categoryLabelKey, optionsKey, optionValueKey]);

  const scrollHighlightedIntoView = useCallback((index) => {
    const element = optionRefs.current[`item-${index}`];
    if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = flatItemsList.length;
      if (totalCount === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter": {
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0 || highlightedIndex >= flatItemsList.length) return;

          const item = flatItemsList[highlightedIndex];
          if (item.type === "select-all-results") {
            handleSelectAllFiltered();
          } else if (item.type === "group") {
            handleCategoryToggle(item.catKey);
          } else if (item.type === "option") {
            handleSelect(item.optValue);
          }
          break;
        }

        case " ": {
          if (e.target.tagName === "INPUT") return;
          e.preventDefault();
          e.stopPropagation();

          if (highlightedIndex < 0 || highlightedIndex >= flatItemsList.length) return;

          const spaceItem = flatItemsList[highlightedIndex];
          if (spaceItem.type === "select-all-results") {
            handleSelectAllFiltered();
          } else if (spaceItem.type === "option") {
            handleSelect(spaceItem.optValue);
          }
          break;
        }

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [
      open,
      flatItemsList,
      highlightedIndex,
      close,
      scrollHighlightedIntoView,
      handleSelectAllFiltered,
      handleCategoryToggle,
      handleSelect,
    ]
  );

  // flatIndex is mutated during render to map visual positions to keyboard navigation indices;
  // when search is active, index 0 is already consumed by the "Select All Results" row
  let flatIndex = isSearchActive ? 0 : -1;

  return (
    <SelectFieldShell
      dropdown={dropdown}
      elemId={instanceId}
      label={label}
      shrink={shrink}
      enableFullWidth={enableFullWidth}
      preserveListFocus
      valueContainerSx={{
        flexWrap: "wrap",
        overflowX: "auto",
        overflowY: "hidden",
        gap: 0.5,
        whiteSpace: "nowrap",
        "&::-webkit-scrollbar": { height: 6 },
      }}
      errorContent={
        fieldErrors ? (
          <FormHelperText sx={{ color: SELECT_COLOR, mt: 0.5 }}>{fieldErrors}</FormHelperText>
        ) : null
      }
      onAnchorClick={() => setOpen((prev) => !prev)}
      onAnchorKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setOpen(true);
        }
        if (event.key === "Escape") close();
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          if (!open) setOpen(true);
        }
      }}
      onListKeyDown={handleListKeyDown}
      contentWidth={dropdownContentWidth}
      valueDisplay={selectedValues.map((v) => (
        <Chip
          key={String(v)}
          size="small"
          label={String(v)}
          onClick={(e) => e.stopPropagation()}
          onDelete={(e) => {
            e.stopPropagation();
            handleRemove(v);
          }}
          deleteIcon={<Cancel sx={{ color: SELECT_COLOR }} />}
          sx={{
            flex: "0 0 auto",
            backgroundColor: SELECT_COLOR,
            color: "#fff",
            fontSize: "12px",
            height: 24,
            "& .MuiChip-deleteIcon": { color: "#fff" },
          }}
        />
      ))}
    >
      {/* Bulk selection row for all currently filtered options */}
      {isSearchActive && (
        <StyledMenuItemContainer
          ref={(el) => {
            optionRefs.current["item-0"] = el;
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleSelectAllFiltered();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={() => setHighlightedIndex(0)}
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor:
              highlightedIndex === 0 ? SELECT_HIGHLIGHT_COLOR : "transparent",
          }}
        >
          <StyledCheckbox
            checked={allFilteredSelected}
            indeterminate={someFilteredSelected}
            onMouseDown={(e) => e.stopPropagation()}
          />
          <StyledMenuListItemText primary="Select All Results" />
        </StyledMenuItemContainer>
      )}

      {optionsToList.map((cat) => {
        const categoryLabel = String(cat?.[categoryLabelKey] ?? "");
        const catKey = categoryLabel;
        const allOptionsRaw = Array.isArray(cat?.[optionsKey]) ? [...cat[optionsKey]] : [];
        const allOptions = allOptionsRaw.sort((a, b) => {
          const av = String(a?.[optionValueKey] ?? "");
          const bv = String(b?.[optionValueKey] ?? "");
          return av.localeCompare(bv, undefined, { numeric: true });
        });
        const optionValues = allOptions
          .map((o) => o?.[optionValueKey])
          .filter((x) => x !== undefined && x !== null);

        const selectedCount = optionValues.filter((v) => isSelected(v)).length;
        const allSelected = optionValues.length > 0 && selectedCount === optionValues.length;
        const someSelected = selectedCount > 0 && !allSelected;
        const isExpanded = !!expanded[catKey];

        flatIndex++;
        const groupIndex = flatIndex;

        return (
          <Box
            key={catKey}
            ref={(el) => { itemRefs.current[catKey] = el; }}
            sx={{ width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <StyledMenuItemContainer
              ref={(el) => { optionRefs.current[`item-${groupIndex}`] = el; }}
              onClick={() => handleCategoryToggle(catKey)}
              onMouseEnter={() => setHighlightedIndex(groupIndex)}
              sx={{
                display: "flex",
                alignItems: "center",
                marginRight: 0,
                backgroundColor:
                  highlightedIndex === groupIndex
                    ? SELECT_HIGHLIGHT_COLOR
                    : "transparent",
              }}
            >
              <StyledCheckbox
                checked={allSelected}
                indeterminate={someSelected}
                onClick={(e) => handleCategorySelect(optionValues, allSelected, e)}
                onMouseDown={(e) => e.stopPropagation()}
              />
              <StyledMenuListItemText primary={categoryLabel} />
              {isExpanded ? (
                <ExpandLessIcon fontSize="small" sx={{ ml: 1 }} />
              ) : (
                <ExpandMoreIcon fontSize="small" sx={{ ml: 1 }} />
              )}
            </StyledMenuItemContainer>

            <Collapse in={isExpanded} timeout={SELECT_MOTION_MS} unmountOnExit>
              {allOptions.map((opt) => {
                const optValue = opt?.[optionValueKey];
                const optLabel = opt?.[optionLabelKey];

                if (optValue === undefined || optValue === null) return null;

                flatIndex++;
                const optIndex = flatIndex;

                return (
                  <StyledSubMenuItemContainer
                    key={String(optValue)}
                    ref={(el) => { optionRefs.current[`item-${optIndex}`] = el; }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(optValue);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onMouseEnter={() => setHighlightedIndex(optIndex)}
                    sx={{
                      pl: 5,
                      display: "flex",
                      alignItems: "center",
                      marginRight: 0,
                      backgroundColor:
                        highlightedIndex === optIndex
                          ? SELECT_HIGHLIGHT_COLOR
                          : "transparent",
                    }}
                  >
                    <StyledCheckbox
                      checked={isSelected(optValue)}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                    <StyledMenuListItemText
                      primary={`${optValue} - ${optLabel ?? ""}`.trim()}
                    />
                  </StyledSubMenuItemContainer>
                );
              })}
            </Collapse>
          </Box>
        );
      })}

      {optionsToList.length === 0 && (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <StyledMenuListItemText primary="No options found" />
        </Box>
      )}
    </SelectFieldShell>
  );
};

export default CustomizedHierarchicalMultiselect;
